import { useRef } from 'react'
import MessageList from './MessageList'
import MessageInput from './MessageInput'
import WelcomePage from './WelcomePage'
import FooterBar from '../layout/FooterBar'
import { useSendMessage } from '@/hooks/useSendMessage'
import i18n from '@/i18n'
import { useJurisdictions } from '@/hooks/useJurisdictions'
import { usePubAuthorities } from '@/hooks/usePubAuthorities'
import { Citation, CitationSource, StreamCompleteResult } from '@/api/chatService'
import { FeedbackFilterConfig } from '@/api/feedbackService'

export interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: Date
  isStreaming?: boolean
  statusText?: string | null
  citations?: Citation[] | null
  sources?: CitationSource[] | null
  feedbackType?: 'positive' | 'negative' | null
}

interface ChatInterfaceProps {
  messages: Message[]
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>
  selectedJurisdictionUuids: string[]
  selectedPubAuthorityUuids: string[]
  selectedDocumentLegislationUuids: string[]
  onJurisdictionError: () => void
  currentConversationId: number | null
  onStreamComplete: (result: StreamCompleteResult) => void
  isLoadingMessages?: boolean
  documentTitleMap?: Map<string, string>
  onCitationClick?: (source: CitationSource, citedText: string, positionFraction: number | null) => void
}

export default function ChatInterface({
  messages,
  setMessages,
  onCitationClick,
  selectedJurisdictionUuids,
  selectedPubAuthorityUuids,
  selectedDocumentLegislationUuids,
  onJurisdictionError,
  currentConversationId,
  onStreamComplete,
  isLoadingMessages,
  documentTitleMap = new Map(),
}: ChatInterfaceProps) {
  // Ref to track the ID of the currently streaming assistant message
  const streamingMessageId = useRef<string | null>(null)

  // Jurisdictions data for UUID → name code mapping
  const { data: jurisdictions = [] } = useJurisdictions()

  // Pub authorities data for enriched feedback filter config
  const { data: pubAuthorities = [] } = usePubAuthorities({
    jurisdictionUuids: selectedJurisdictionUuids,
    enabled: selectedJurisdictionUuids.length > 0,
  })

  // Build enriched filter config with human-readable names for feedback
  const enrichedFilterConfig: FeedbackFilterConfig = {
    jurisdictions: selectedJurisdictionUuids.map(uuid => ({
      uuid,
      name: jurisdictions.find(j => j.jurisdiction_uuid === uuid)?.jurisdiction_common_name || uuid,
    })),
    pub_authorities: selectedPubAuthorityUuids.map(uuid => ({
      uuid,
      name: pubAuthorities.find(pa => pa.uuid === uuid)?.name || uuid,
    })),
    documents: selectedDocumentLegislationUuids.map(uuid => ({
      legislation_uuid: uuid,
      title: documentTitleMap.get(uuid) || uuid,
    })),
  }

  const accumulatedContentRef = useRef('')

  const { sendMessage, isStreaming } = useSendMessage({
    onStatus: (status: string) => {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === streamingMessageId.current
            ? { ...msg, statusText: status }
            : msg,
        ),
      )
    },
    onChunk: (content: string) => {
      accumulatedContentRef.current += content
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === streamingMessageId.current
            ? { ...msg, content: accumulatedContentRef.current, statusText: null }
            : msg,
        ),
      )
    },
    onComplete: (result: StreamCompleteResult) => {
      const id = streamingMessageId.current
      streamingMessageId.current = null
      const finalContent = result.citedResponse || accumulatedContentRef.current
      accumulatedContentRef.current = ''
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === id
            ? {
                ...msg,
                isStreaming: false,
                statusText: null,
                id: result.messageId.toString(),
                content: finalContent,
                citations: result.citations,
                sources: result.sources,
              }
            : msg,
        ),
      )
      onStreamComplete(result)
    },
    onError: (error: Error) => {
      const id = streamingMessageId.current
      streamingMessageId.current = null
      const content = error.name === 'RefusalError'
        ? error.message
        : i18n.t('chat.errorMessage')
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === id
            ? { ...msg, isStreaming: false, content }
            : msg,
        ),
      )
    },
  })

  const handleSendMessage = (content: string): boolean => {
    // Validate jurisdiction selection
    if (selectedJurisdictionUuids.length === 0) {
      onJurisdictionError()
      return false
    }

    // Reset accumulated content for new message
    accumulatedContentRef.current = ''

    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content,
      timestamp: new Date(),
    }

    // Create placeholder assistant message for streaming
    const placeholderId = (Date.now() + 1).toString()
    const assistantMessage: Message = {
      id: placeholderId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isStreaming: true,
    }

    streamingMessageId.current = placeholderId
    setMessages((prev) => [...prev, userMessage, assistantMessage])

    // Map jurisdiction UUIDs → name codes for the backend
    const jurisdictionNames = selectedJurisdictionUuids
      .map((uuid) => jurisdictions.find((j) => j.jurisdiction_uuid === uuid)?.jurisdiction_name)
      .filter((name): name is string => name != null)

    sendMessage({
      message: content,
      jurisdictionNames,
      jurisdictionUuids: selectedJurisdictionUuids,
      pubAuthorityUuids: selectedPubAuthorityUuids,
      documentLegislationUuids: selectedDocumentLegislationUuids,
      conversationId: currentConversationId,
      language: i18n.language,
    })
    return true
  }

  const showWelcome = messages.length === 0 && !isStreaming && !isLoadingMessages

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-white">
      {showWelcome ? (
        <WelcomePage onSendMessage={handleSendMessage} />
      ) : (
        <>
          <MessageList messages={messages} conversationId={currentConversationId} filterConfig={enrichedFilterConfig} onCitationClick={onCitationClick} />
          <MessageInput onSend={handleSendMessage} disabled={isStreaming || isLoadingMessages} />
        </>
      )}
      <FooterBar />
    </div>
  )
}
